import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Typography,
  useTheme,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { InfoCard } from '@backstage/core-components';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { RebacExplorerClient } from '../../api/RebacExplorerClient';
import type { Grant } from '../../api/RebacExplorerClient';
import { GrantsGrid } from './GrantsGrid';

interface TemplateAccessCardProps {
  templateId: string;
  /** When true, triggers a reload (e.g. after TemplateManageButton mutates). */
  refreshSignal?: number;
}

export function TemplateAccessCard({ templateId, refreshSignal }: TemplateAccessCardProps) {
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const client = new RebacExplorerClient({ discoveryApi, fetchApi });

  const [grants, setGrants] = useState<Grant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await client.getGrants('template', templateId);
      setGrants(data.grants);
    } catch (e: any) {
      setError(e.message ?? 'Erro ao carregar permissões');
    } finally {
      setLoading(false);
    }
  }, [templateId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // Reload when parent signals a mutation happened
  useEffect(() => {
    if (refreshSignal !== undefined && refreshSignal > 0) load();
  }, [refreshSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalCount = grants.length;

  let summary: string | null;
  if (loading || error) {
    summary = null;
  } else if (totalCount === 0) {
    summary = 'Nenhuma permissão explícita além do Platform Team.';
  } else {
    const suffix = totalCount === 1 ? 'ão' : 'ões';
    const agreement = totalCount === 1 ? '' : 's';
    summary = `${totalCount} permiss${suffix} configurada${agreement}`;
  }

  return (
    <InfoCard title="Acesso ao Template">
      {loading && (
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="textSecondary">Carregando…</Typography>
        </Box>
      )}
      {!loading && error && (
        <Typography
          variant="body2"
          style={{
            color: theme.palette.error.main,
            padding: '8px 12px',
            borderRadius: 4,
            border: `1px solid ${theme.palette.error.main}`,
            backgroundColor: isDark ? 'rgba(211,47,47,0.12)' : '#ffebee',
          }}
        >
          {error}
        </Typography>
      )}
      {!loading && !error && (
        <Box>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="body2" color="textSecondary">
              {summary}
            </Typography>
            {totalCount > 0 && (
              <Button
                size="small"
                onClick={() => setExpanded(v => !v)}
                endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                style={{ textTransform: 'none', fontSize: 12, color: theme.palette.text.secondary }}
              >
                {expanded ? 'Ocultar' : 'Ver quem tem acesso'}
              </Button>
            )}
          </Box>

          <Collapse in={expanded} unmountOnExit>
            <Box style={{ marginTop: 16 }}>
              <GrantsGrid
                grants={grants}
                canManage={false}
                canDelete={false}
                onRevoke={() => {}}
              />
            </Box>
          </Collapse>
        </Box>
      )}
    </InfoCard>
  );
}
